import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from 'src/app/hooks';
import { setIsRoomComplete } from 'src/app/reducers/room/room.reducer';
import type { RoomTask } from 'src/common/interfaces/tasks';
import { useIsTaskBreakdownSupported } from '../hooks/use-task-breakdown';
type RoomTaskBreakdownContextType = {
  currentTask?: RoomTask;
  firstIncompleteTask?: RoomTask;
  advanceToNextTask: VoidFunction;
  resetToFirstTask: VoidFunction;
  proceedToTaskNo: (taskNo: number) => void;
  completeRoom: VoidFunction;
  isTaskBreakdownSupported: boolean;
  isLastTask: boolean;
  progressByCurrentTask: number;
  shouldAutoTerminateVM: boolean;
};
const RoomTaskBreakdownContext = createContext<RoomTaskBreakdownContextType>({
  advanceToNextTask: () => {},
  resetToFirstTask: () => {},
  proceedToTaskNo: () => {},
  completeRoom: () => {},
  isLastTask: false,
  isTaskBreakdownSupported: false,
  progressByCurrentTask: 0,
  shouldAutoTerminateVM: true
});
export const RoomTaskBreakdownContextProvider: React.FC<PropsWithChildren<{
  tasks?: RoomTask[];
  roomCode: string;
  setIsCongratulationsFullScreenOpen: (arg: boolean) => void;
}>> = ({
  children,
  tasks,
  roomCode,
  setIsCongratulationsFullScreenOpen
}) => {
  const dispatch = useAppDispatch();
  const [currentTask, setCurrentTask] = useState<RoomTask>();
  const [firstIncompleteTask, setFirstIncompleteTask] = useState<RoomTask>();
  const sortedTasks = useMemo(() => [...(tasks || [])].sort((task1, task2) => task1.taskNo - task2.taskNo), [tasks]);
  const isTaskBreakdownSupported = useIsTaskBreakdownSupported(roomCode);
  const completeRoom = useCallback(() => {
    dispatch(setIsRoomComplete(true));
    setIsCongratulationsFullScreenOpen(true);
  }, [dispatch, setIsCongratulationsFullScreenOpen]);
  const progressByCurrentTask = useMemo(() => {
    if (!currentTask || !tasks || !tasks.length) {
      return 0;
    }
    return 100 / tasks.length * (currentTask.taskNo - 1);
  }, [currentTask, tasks]);
  useEffect(() => {
    if (!currentTask) {
      const task = sortedTasks.find(t => t.questions.find(q => !q.progress?.correct)) || sortedTasks[sortedTasks.length - 1];
      setCurrentTask(task);
      setFirstIncompleteTask(task);
    }
  }, [currentTask, sortedTasks]);
  const advanceToNextTask = useCallback(() => {
    const nextTask = sortedTasks.find(t => currentTask && t.taskNo === currentTask.taskNo + 1);
    setCurrentTask(nextTask);
    setFirstIncompleteTask(nextTask);
  }, [currentTask, sortedTasks]);
  const resetToFirstTask = useCallback(() => {
    setCurrentTask(sortedTasks[0]);
    setFirstIncompleteTask(sortedTasks[0]);
  }, [sortedTasks]);
  const proceedToTaskNo = useCallback((taskNo: number) => setCurrentTask(sortedTasks.find(t => t.taskNo === taskNo)), [sortedTasks]);
  const shouldAutoTerminateVM = useMemo(() => roomCode !== 'offensivesecurityintrokK', [roomCode]);
  const contextValue = useMemo(() => ({
    currentTask: sortedTasks.find(t => t.taskNo === currentTask?.taskNo),
    advanceToNextTask,
    resetToFirstTask,
    proceedToTaskNo,
    isLastTask: tasks && currentTask ? currentTask.taskNo === sortedTasks[tasks.length - 1]?.taskNo : false,
    progressByCurrentTask,
    isTaskBreakdownSupported,
    firstIncompleteTask,
    completeRoom,
    shouldAutoTerminateVM
  }), [sortedTasks, advanceToNextTask, resetToFirstTask, proceedToTaskNo, tasks, currentTask, progressByCurrentTask, isTaskBreakdownSupported, firstIncompleteTask, completeRoom, shouldAutoTerminateVM]);
  return <RoomTaskBreakdownContext.Provider value={contextValue} data-sentry-element="unknown" data-sentry-component="RoomTaskBreakdownContextProvider" data-sentry-source-file="room-task-breakdown-context.tsx">{children}</RoomTaskBreakdownContext.Provider>;
};
export const useRoomTaskBreakdownContext = () => useContext(RoomTaskBreakdownContext);